/**
 * Effect preset registry — populated by addons in M2.
 * M1: built-in "none" only; registry API for future presets.
 */
import { shallowReactive, computed, type ComputedRef } from 'vue';

export interface VeyopEffectPreset {
  id: string;
  label: string;
  /** CSS filter fragments applied after adjustments. */
  cssFilters: string[];
  priority?: number;
}

const effectPresets = shallowReactive(new Map<string, VeyopEffectPreset>());

function byPriority<T extends { priority?: number }>(entries: Iterable<T>): T[] {
  return [...entries].sort((a, b) => Number(a.priority ?? 100) - Number(b.priority ?? 100));
}

export function registerEffectPreset(preset: VeyopEffectPreset): () => void {
  if (!preset?.id) return () => {};
  effectPresets.set(preset.id, preset);
  return () => {
    if (effectPresets.get(preset.id) === preset) {
      effectPresets.delete(preset.id);
    }
  };
}

export function getEffectPresets(): VeyopEffectPreset[] {
  return byPriority(effectPresets.values());
}

export function findEffectPreset(id: string): VeyopEffectPreset | null {
  return effectPresets.get(id) ?? null;
}

export function useEffectPresets(): ComputedRef<VeyopEffectPreset[]> {
  return computed(() => byPriority(effectPresets.values()));
}

export function resolveEffectPresetFilters(presetId: string): string[] {
  if (!presetId || presetId === 'none') return [];
  const preset = findEffectPreset(presetId);
  return preset?.cssFilters ?? [];
}
